import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Stack, SxProps, Typography } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, ReactElement, createRef } from 'react';
import { FilterStatusBadge } from '../../../../common/components/FilterStatusBadge';
import { PaginatedTableState } from '../../../../common/controllers/PaginatedTableController';
import { getFilterStatusLabel } from '../../../../common/enums/FilterStatus';
import { SEVERITY_COLORS, Severity, getLabel } from '../../../../common/enums/Severity';
import { MultiSelectItemCard } from '../../../../common/instances/components/ItemCard';
import { SeverityBadge } from '../../../../common/instances/components/SeverityBadge';
import { InstancesTableSkeleton } from '../../../../common/instances/components/skeletons/InstancesTableSkeleton';
import { Props as BaseProps, InstancesTableController, getInstanceClassname } from '../../../../common/instances/controllers/InstancesTableController';
import { Instance } from '../../../../common/instances/models/Instance';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../../common/types/pagination';
import { bind } from '../../../../common/utils/decorators';
import { UrlHelper } from '../../../../common/utils/UrlHelper';
import { FeatureFlagsType, getFeatureFlag } from '../../../../config/launch-darkly/FeatureFlagsController';
import { CheckedStatus, ReportExportState } from '../../../../exports/triggers/states/ReportExportState';
import { PATHS } from '../../../../routing/routes';
import { SeverityFilter } from '../../common/components/SeverityFilter';
import { DEFAULT_INSTANCES_LIST_PAGE_SIZE, ReportIssueRepository } from '../services/ReportIssueRepository';

type Props = BaseProps & {
  issueNumber: number;
  selectedInstanceNumber: number | null;
  includeFilteredItems: boolean;
  severityFilters?: SeverityFilter[] | null;
  onInstanceFilteredOut: () => void;
};

type State = PaginatedTableState & {};

const INSTANCES_BACKGROUNDS_SX = (current: number): SxProps => {
  return {
    [`& [class*="instance-${current}-"]`]: { '& .instance-data': { textDecoration: 'underline' } },
    [`& .instance-${current}-${Severity.HIGH}`]: { backgroundColor: SEVERITY_COLORS[Severity.HIGH].fadedBackground },
    [`& .instance-${current}-${Severity.MED}`]: { backgroundColor: SEVERITY_COLORS[Severity.MED].fadedBackground },
    [`& .instance-${current}-${Severity.LOW}`]: { backgroundColor: SEVERITY_COLORS[Severity.LOW].fadedBackground },
    [`& .instance-${current}-${Severity.PASSED}`]: { backgroundColor: SEVERITY_COLORS[Severity.PASSED].fadedBackground },
  };
};

export class InstancesListController extends InstancesTableController<Props> {
  private readonly repository = new ReportIssueRepository();
  private readonly exportState = ReportExportState.getInstance();

  private selectedSeverities = [Severity.HIGH, Severity.MED, Severity.LOW, Severity.PASSED];
  private selfRef = createRef<HTMLDivElement | null>();

  private filtersHaveChanged = false;

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    if (prevState.initialDataLoad && !this.state.initialDataLoad && this.props.selectedInstanceNumber) {
      // The initial instances list has been loaded and an instance is selected.
      // Scroll to it so it is clearly displayed in the page.
      // `setInterval` is necessary to account for the rendering delay of the table component:
      // rows are not rendered all together, and rendering begins `after` the `initialDataLoad`
      // is set to `false`
      const interval = setInterval(() => {
        if (this.scrollToInstance()) {
          clearInterval(interval);
        }
      }, 100);
    }
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_INSTANCES_LIST_PAGE_SIZE;
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Instance>> {
    try {
      const result = await this.repository.getInstances(
        this.props.reportId,
        this.props.issueNumber,
        this.props.includeFilteredItems,
        this.selectedSeverities,
        cursorInfo
      );
      if (this.filtersHaveChanged) {
        this.filtersHaveChanged = false;
        if (this.props.selectedInstanceNumber) {
          // Notify the parent if there is an instance displayed,
          // but that instance is removed from the table after the user
          // changed the severity filter
          const selectedInstanceIsInResult = result.data.some((i) => i.getNumber() === this.props.selectedInstanceNumber);
          if (!selectedInstanceIsInResult) {
            this.props.onInstanceFilteredOut();
          }
        }
      }
      return result;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected renderTable(tableComponent: ReactElement) {
    if (this.state.initialDataLoad) {
      // Use a custom skeleton instead of the built-in automatic skeleton.
      // Each row is a `ItemCard` with its own internal layout
      return <InstancesTableSkeleton rows={this.getDefaultPageSize()} />;
    }
    const severityBackgrounds = INSTANCES_BACKGROUNDS_SX(this.props.selectedInstanceNumber ?? 0);
    return (
      <Box className="instances-list" ref={this.selfRef} sx={severityBackgrounds}>
        {tableComponent}
      </Box>
    );
  }

  private handleInstanceClick(e: ChangeEvent<HTMLInputElement>, model: Instance) {
    if (model.getSeverity() === Severity.PASSED) {
      return;
    }
    if (e.target.checked) {
      this.exportState.selectInstance(model.getId(), model.getIssueNumber());
    } else {
      this.exportState.unselectInstance(model.getId(), model.getIssueNumber());
    }
  }

  private handleCheckAllInstances(e: ChangeEvent<HTMLInputElement>) {
    const instanceIds = this.data.filter((i) => i.getSeverity() !== Severity.PASSED).map((i) => i.getId());
    const rule = this.data[0].getIssueNumber();
    if (e.target.checked) {
      this.exportState.selectAllInstances(instanceIds, rule);
    } else {
      this.exportState.unselectAllInstances(instanceIds, rule);
    }
  }

  @bind
  protected renderHeader() {
    const { severityFilters } = this.props;
    const canMultiSelectExport = getFeatureFlag(FeatureFlagsType.ENABLE_JIRA_BATCH_EXPORT);
    const availableIds = this.data.filter((i) => i.getSeverity() !== Severity.PASSED).map((i) => i.getId());
    const selectedStatus = this.exportState.getIssueSelectionState(availableIds, this.props.issueNumber);
    return (
      <Box sx={{ width: 1 }} data-role="paginated-table-toolbar">
        <Box sx={{ height: '4rem', px: 2, display: 'flex', alignItems: 'center' }}>
          {canMultiSelectExport && availableIds.length > 0 && (
            <Checkbox
              indeterminate={selectedStatus === CheckedStatus.PARTIALLY_SELECTED}
              checked={selectedStatus === CheckedStatus.SELECTED}
              onChange={(e) => this.handleCheckAllInstances(e)}
              data-role="select-all-instances"
              disabled={availableIds.length === 0}
              inputProps={{ 'aria-label': t('a11y.report.issues.selectAllInstances', { issueNumber: this.props.issueNumber }) }}
            />
          )}
          {this.renderLeftToolbarElements()}
        </Box>
        {severityFilters && <SeverityFilter severities={severityFilters} onChange={this.onSeverityFilterChanged} />}
      </Box>
    );
  }

  protected renderInstanceRow(model: Instance, tabIndex: 0 | -1) {
    const canMultiSelectExport = getFeatureFlag(FeatureFlagsType.ENABLE_JIRA_BATCH_EXPORT);
    if (!canMultiSelectExport) {
      return super.renderInstanceRow(model, tabIndex);
    }

    return (
      <MultiSelectItemCard className={getInstanceClassname(model)} borderLeftColor={SEVERITY_COLORS[model.getSeverity()].main} grow={true}>
        <Stack direction="row" spacing={1.5} alignItems="center" width={1}>
          {canMultiSelectExport && model.getSeverity() !== Severity.PASSED && (
            <Checkbox
              checked={this.exportState.getInstanceSelectionState(model.getId(), model.getIssueNumber()) === CheckedStatus.SELECTED}
              onChange={(e) => this.handleInstanceClick(e, model)}
              data-role={`instance-${model.getId()}-checkbox`}
              inputProps={{
                'aria-label': t('a11y.report.issues.selectInstance', { issueNumber: model.getIssueNumber(), numberLocal: model.getNumberLocal() }),
              }}
            />
          )}
          <SeverityBadge severity={model.getSeverity()}>
            {model.getIssueNumber()}.{model.getNumberLocal()}
          </SeverityBadge>
          <Stack direction="column" flexGrow={1}>
            <FilterStatusBadge filterStatus={model.getFilterStatus()} noBorder sx={{ justifyContent: 'flex-start' }} />
            <Typography className="instance-data" sx={{ pt: 0.25, wordBreak: 'break-word' }}>
              {model.getData()}
            </Typography>
          </Stack>
          <IconButton
            sx={{ ml: 'auto' }}
            aria-label={t('a11y.report.issues.instanceListItem', {
              instanceNumber: model.getNumber(),
              instanceData: model.getData(),
              severity: getLabel(model.getSeverity()),
              filterStatus: getFilterStatusLabel(model.getFilterStatus(), { omitFullyKept: true }),
            })}
            href={UrlHelper.generatePath(
              PATHS.REPORT_ISSUE_INSTANCE,
              {
                reportId: this.props.reportId.toString(),
                issueId: model.getIssueNumber().toString(),
                instanceId: model.getNumber().toString(),
              },
              { keepQueryParams: true }
            )}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Stack>
      </MultiSelectItemCard>
    );
  }

  private scrollToInstance() {
    const selectedInstanceCard = this.selfRef.current?.querySelector(`[data-id="${this.props.selectedInstanceNumber}"]`);
    if (!selectedInstanceCard) {
      // The instance card has not been rendered yet, return `false` to keep the interval going
      return false;
    }
    // The card has been rendered, give it a best-effort waiting time, try to scroll
    // it to the middle of the screen, and return `true` to stop the interval
    setTimeout(() => selectedInstanceCard.scrollIntoView({ block: 'center' }), 300);
    return true;
  }

  @bind
  private onSeverityFilterChanged(severities: Severity[]) {
    this.filtersHaveChanged = true;
    this.selectedSeverities = severities;
    this.reloadTableContent();
  }
}
